'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence } from 'framer-motion';
import { Toast, ToastType } from '../components/ui/Toast';
import { v4 as uuidv4 } from 'uuid'; // Actually we don't have uuid in devDeps maybe, let's use simple random
// Re-check package.json: uuid is NOT there. I will use Math.random.

type ToastItem = {
	id: string;
	message: string;
	type: ToastType;
};

type ToastContextValue = {
	toast: {
		success: (msg: string) => void;
		error: (msg: string) => void;
		warning: (msg: string) => void;
		info: (msg: string) => void;
	};
};

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
	const [toasts, setToasts] = useState<ToastItem[]>([]);

	const addToast = useCallback((message: string, type: ToastType) => {
		const id = Math.random().toString(36).substring(2, 9);
		setToasts((prev) => [...prev, { id, message, type }]);
	}, []);

	const removeToast = useCallback((id: string) => {
		setToasts((prev) => prev.filter((t) => t.id !== id));
	}, []);

	const value = {
		toast: {
			success: (msg: string) => addToast(msg, 'success'),
			error: (msg: string) => addToast(msg, 'error'),
			warning: (msg: string) => addToast(msg, 'warning'),
			info: (msg: string) => addToast(msg, 'info'),
		},
	};

	return (
		<ToastContext.Provider value={value}>
			{children}
			{/* Portal for Toasts */}
			{typeof document !== 'undefined' &&
				createPortal(
					<div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
						<AnimatePresence>
							{toasts.map((t) => (
								<Toast
									key={t.id}
									id={t.id}
									message={t.message}
									type={t.type}
									onDismiss={removeToast}
								/>
							))}
						</AnimatePresence>
					</div>,
					document.body
				)}
		</ToastContext.Provider>
	);
}

export function useToast() {
	const ctx = useContext(ToastContext);
	if (!ctx) throw new Error('useToast must be used within ToastProvider');
	return ctx.toast;
}
